import { format } from "date-fns";
import type { Locale } from "date-fns";
import { ConnectProto } from "@racoon/api";
import type { Column } from "@racoon/ui";
import { Namespace, TFn, TranslationKey } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";
import { fullName } from "~/shared/helpers";
import { exportToExcel, generateFilename } from "~/shared/lib/export-utils";
import { jobTypeLabel } from "./jobTypeLabel";

/** One spreadsheet line: a job plus the route context it belongs to. */
type RouteJobExportRow = {
  route: ConnectProto.Route;
  job: ConnectProto.Job;
  /** 1-based position of the job inside the route */
  jobIndex: number;
};

// Mirrors the photo-report icon logic: a report counts as present only when
// both the "service started" and "service ended" photos exist.
const hasFileReport = (job: ConnectProto.Job, type: ConnectProto.FileUploadType) =>
  (job.fileReports ?? []).some((file) => file.type === type);

const hasPhotoReport = (job: ConnectProto.Job) => {
  const hasStarted =
    Boolean(job.serviceStartedReportUrl) ||
    hasFileReport(job, ConnectProto.FileUploadType.JOB_SERVICE_STARTED);
  const hasEnded =
    Boolean(job.serviceEndedReportUrl) ||
    hasFileReport(job, ConnectProto.FileUploadType.JOB_SERVICE_ENDED);

  return hasStarted && hasEnded;
};

/**
 * Every column of the route jobs table, plus the route context columns that a
 * standalone file needs (route, driver, plate number).
 */
const getRouteJobsExportColumns = (t: TFn, dateFnsLocale: Locale): Column<RouteJobExportRow>[] => [
  {
    id: "route",
    header: t(Namespace.Routes, "colRoute"),
    accessor: (row) => `M${String(row.route.id)}`,
  },
  {
    id: "driver",
    header: t(Namespace.Routes, "driver"),
    accessor: (row) => fullName(row.route.driverOnShift?.driver) || "-",
  },
  {
    id: "plateNumber",
    header: t(Namespace.Routes, "colPlateNumber"),
    accessor: (row) => row.route.driverOnShift?.truck?.plateNumber ?? "-",
  },
  {
    id: "order",
    header: "#",
    accessor: (row) => row.jobIndex,
  },
  {
    id: "pointId",
    header: t(Namespace.Routes, "colJobId"),
    accessor: (row) => `Z${String(row.job.id)}`,
  },
  {
    id: "statusLabel",
    header: t(Namespace.Routes, "colStatus"),
    accessor: (row) =>
      t(Namespace.Enum, `JOB_STATUS_${ConnectProto.JobStatus[row.job.status]}` as TranslationKey),
  },
  {
    id: "address",
    header: t(Namespace.Routes, "colAddress"),
    accessor: (row) => row.job.pickupLocation?.name ?? "-",
  },
  {
    id: "jobType",
    header: t(Namespace.Routes, "colJobType"),
    accessor: (row) => jobTypeLabel(row.job.type, t),
  },
  {
    id: "customer",
    header: t(Namespace.Routes, "colCustomer"),
    accessor: (row) => row.job.serviceReceiver?.name ?? "-",
  },
  {
    id: "containerGroup",
    header: t(Namespace.Routes, "colContainerGroup"),
    accessor: (row) => row.job.containerType?.name ?? "-",
  },
  {
    id: "volume",
    header: `${t(Namespace.Routes, "colVolume")}, ${t(Namespace.Routes, "cubicMetersLabel")}`,
    // Kept numeric so the column stays summable in Excel.
    accessor: (row) => row.job.containerType?.volume ?? "-",
  },
  {
    id: "doneAt",
    header: t(Namespace.Routes, "colDoneAt"),
    accessor: (row) => {
      const date = row.job.factEndAt ? fromTimestamp(row.job.factEndAt) : null;
      return date ? format(date, "HH:mm dd MMM yyyy", { locale: dateFnsLocale }) : "-";
    },
  },
  {
    id: "hasPhotoReport",
    header: t(Namespace.Routes, "colPhotoReport"),
    accessor: (row) =>
      hasPhotoReport(row.job) ? t(Namespace.Routes, "yes") : t(Namespace.Routes, "no"),
  },
];

const buildRouteJobsExportRows = (route: ConnectProto.Route): RouteJobExportRow[] =>
  (route.jobs ?? []).map((job, index) => ({
    route,
    job,
    jobIndex: index + 1,
  }));

/** Download a single route's jobs as an .xlsx file with every table column. */
export const exportRouteJobsToExcel = (params: {
  route: ConnectProto.Route;
  t: TFn;
  dateFnsLocale: Locale;
  date?: Date;
}): void => {
  const { route, t, dateFnsLocale, date } = params;

  exportToExcel({
    data: buildRouteJobsExportRows(route),
    columns: getRouteJobsExportColumns(t, dateFnsLocale),
    filename: generateFilename(`route_M${String(route.id)}`, date),
    sheetName: `M${String(route.id)}`,
  });
};
